import type { WidgetApi } from '@/_api';
import { hc } from 'hono/client';
import {
  FoxwayGradingForm,
  FoxwayModel,
  FoxwayPriceQuote,
  FoxwayVerifyModelError,
} from '@/lib/types/foxway';
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import {
  OnlinePartnerId,
  OnlinePartnerTradeIn,
  OnlinePartnerClientConfig,
  OnlinePartnerClientCampaign,
} from '@/lib/types/onlineTiPartners';
import { DeviceType } from '@/lib/constants/foxway';
import { DEFAULT_LOCALE, LOCALE } from '@/lib/constants/i18n';
import { SESSION_ID_KEY, STATE_PERSIST_KEY } from '../constants';
import { ONLINETI_WIDGET_API_URL } from '@/lib/constants/onlineTI';
import { OnlineTradeInDetails } from '@/lib/types/onlineTi';

type State = {
  partner: OnlinePartnerClientConfig | null;
  campaign: OnlinePartnerClientCampaign | null;
  clientId: string | null;
  orderId: string | null;
  sku: string | null;
  ean: string | null;
  purchaseUrl: string | null;
  campaignCode: string | null;

  small: boolean;

  selectedDeviceType: DeviceType | null;

  models: FoxwayModel[];
  modelsQuery: string;
  modelsAbortController?: AbortController;
  loadingModels: boolean;
  selectedModel: FoxwayModel | null;

  deviceIdentifier: string;
  deviceIdentifierAbortController?: AbortController;
  deviceIdentifierValid: boolean;
  deviceIdentifierError: FoxwayVerifyModelError | null;
  validatingDeviceIdentifier: boolean;

  gradingForm: FoxwayGradingForm | null;
  gradingFormAbortController?: AbortController;
  loadingGradingForm: boolean;
  gradingAnswers: { [questionId: number]: number } | null;
  gradingExtras: { [extraId: number]: boolean } | null;

  priceQuote: FoxwayPriceQuote | null;
  priceQuoteAbortController?: AbortController;
  loadingPriceQuote: boolean;

  details: OnlineTradeInDetails;
  marketingConsent?: boolean;

  tradeIn: OnlinePartnerTradeIn | null;
  loadingTradeIn: boolean;
  postingTradeIn: boolean;

  locale: LOCALE | null;
};

type Actions = {
  getPartner: (partnerId: OnlinePartnerId) => Promise<void>;

  setClientId: (clientId: string | null) => void;
  setOrderId: (orderId: string | null) => void;
  setSku: (sku: string | null) => void;
  setEan: (ean: string | null) => void;
  setPurchaseUrl: (sku: string | null) => void;
  setCampaignCode: (campaignCode: string | null) => void;
  setSmall: (small: boolean) => void;

  setSelectedDeviceType: (selectedDeviceType: DeviceType) => void;

  setModelsQuery: (query: string) => void;
  getModels: () => void;
  setSelectedModel: (selectedModel: FoxwayModel | null) => void;

  setGradingAnswer: (questionId: number, answerId: number) => void;
  setGradingExtra: (extraId: number, answer: boolean) => void;
  getGradingForm: () => void;

  setDeviceIdentifier: (deviceIdentifier: string) => void;
  validateDeviceIdentifier: () => Promise<boolean>;

  getPriceQuote: () => Promise<boolean>;

  setDetails: (details: OnlineTradeInDetails) => void;
  setMarketingConsent: (marketingConsent: boolean) => void;

  registerTradeIn: () => Promise<boolean>;
  getTradeIn: (tradeInId: string | null) => Promise<void>;
  updateTradeIn: (updates: {
    clientId?: string;
    orderId?: string;
  }) => Promise<void>;

  setLocale: (locale: LOCALE | null) => void;
};

const INITIAL_STATE: State = {
  partner: null,
  campaign: null,
  clientId: null,
  orderId: null,
  sku: null,
  ean: null,
  purchaseUrl: null,
  campaignCode: null,

  small: false,

  selectedDeviceType: DeviceType.mobile,

  models: [],
  modelsQuery: '',
  loadingModels: false,
  selectedModel: null,

  gradingForm: null,
  loadingGradingForm: false,
  gradingAnswers: null,
  gradingExtras: null,

  deviceIdentifier: '',
  deviceIdentifierValid: false,
  deviceIdentifierError: null,
  validatingDeviceIdentifier: false,

  priceQuote: null,
  loadingPriceQuote: false,

  details: {},

  loadingTradeIn: false,
  postingTradeIn: false,
  tradeIn: null,

  locale: null,
};

const catchAbortError = (e: any) => {
  if (e.name === 'AbortError') {
    return;
  }
  throw e;
};

export const useStore = create<State & Actions>()(
  devtools(
    persist(
      (set, get) => {
        const apiClient = () => {
          const sku = get().sku;
          const campaign = get().campaignCode;

          const rpcClient = hc<WidgetApi>(ONLINETI_WIDGET_API_URL, {
            headers: {
              'Accept-Language':
                get().locale || get().partner?.defaultLocale || DEFAULT_LOCALE,
              ...(sku && { 'X-sku': sku }),
              ...(campaign && { 'X-campaign': campaign }),
            },
          });
          return rpcClient.widget[':partnerId'];
        };

        return {
          ...INITIAL_STATE,
          getPartner: async (partnerId) => {
            const ean = get().ean;

            const response = await apiClient().config.$get({
              param: {
                partnerId,
              },
              query: {
                ean: ean ?? undefined,
              },
            });

            const { partner, campaign, sku } = await response.json();

            set({
              partner,
              campaign,
              sku,
            });
          },

          setClientId: (clientId) => {
            set({ clientId });
          },
          setOrderId: (orderId) => {
            set({ orderId });
          },
          setSku: (sku) => {
            set({ sku });
          },
          setEan: (ean) => {
            set({ ean });
          },
          setPurchaseUrl: (purchaseUrl) => {
            set({ purchaseUrl });
          },
          setCampaignCode: (campaignCode) => {
            if (campaignCode) {
              set({ campaignCode });
            }
          },
          setSmall: (small) => {
            set({ small });
          },

          setSelectedDeviceType: (selectedDeviceType) => {
            const { modelsAbortController } = get();

            modelsAbortController?.abort();

            set(
              ({
                locale,
                orderId,
                clientId,
                sku,
                ean,
                purchaseUrl,
                campaign,
                campaignCode,
                partner,
                tradeIn,
              }) => ({
                ...INITIAL_STATE,
                locale,
                orderId,
                clientId,
                sku,
                ean,
                purchaseUrl,
                campaign,
                campaignCode,
                partner,
                selectedDeviceType,
                tradeIn,
              }),
            );
          },

          setModelsQuery: (modelsQuery) => {
            set({ modelsQuery });
          },

          getModels: () => {
            const {
              partner,
              selectedDeviceType,
              modelsQuery,
              modelsAbortController,
            } = get();

            if (!partner) return;

            modelsAbortController?.abort();

            if (modelsQuery.length < 3) {
              set({
                models: [],
                loadingModels: false,
              });
              return;
            }

            const controller = new AbortController();
            set({
              loadingModels: true,
              modelsAbortController: controller,
            });

            apiClient()
              .models.$get(
                {
                  param: {
                    partnerId: partner.id,
                  },
                  query: {
                    type: selectedDeviceType ?? undefined,
                    q: modelsQuery,
                  },
                },
                {
                  init: { signal: controller.signal },
                },
              )
              .then((res) => res?.json())
              .then(({ Data }) =>
                set({
                  models: Data,
                  loadingModels: false,
                }),
              )
              .catch(catchAbortError);
          },
          setSelectedModel: (selectedModel) => {
            set(
              ({
                locale,
                orderId,
                clientId,
                sku,
                ean,
                purchaseUrl,
                campaign,
                campaignCode,
                partner,
                modelsQuery,
                models,
                selectedDeviceType,
                deviceIdentifier,
                tradeIn,
              }) => ({
                ...INITIAL_STATE,
                locale,
                orderId,
                clientId,
                sku,
                ean,
                purchaseUrl,
                campaign,
                campaignCode,
                partner,
                modelsQuery,
                models,
                selectedDeviceType,
                selectedModel,
                deviceIdentifier,
                tradeIn,
              }),
            );
          },

          setDeviceIdentifier: (deviceIdentifier) => {
            set({
              deviceIdentifierError: null,
              deviceIdentifier,
              deviceIdentifierValid: false,
            });
          },

          validateDeviceIdentifier: async () => {
            const {
              partner,
              selectedModel,
              deviceIdentifier,
              deviceIdentifierAbortController,
            } = get();

            if (!partner) return false;

            if (selectedModel) {
              deviceIdentifierAbortController?.abort();

              const controller = new AbortController();
              set({
                validatingDeviceIdentifier: true,
                deviceIdentifierAbortController: controller,
              });

              const response = await apiClient()
                ['verify-model'].$get(
                  {
                    param: {
                      partnerId: partner.id,
                    },
                    query: {
                      modelId: selectedModel.Id.toString(),
                      deviceIdentifier,
                    },
                  },
                  { init: { signal: controller.signal } },
                )
                .catch(catchAbortError);

              if (!response?.ok) {
                return false;
              }

              const { Errors } = await response.json();

              if (Errors?.length) {
                set({
                  deviceIdentifierValid: false,
                  deviceIdentifierError: Errors[0],
                  validatingDeviceIdentifier: false,
                });
                return false;
              }

              set({
                deviceIdentifierValid: true,
                deviceIdentifierError: null,
                validatingDeviceIdentifier: false,
              });

              return true;
            }

            return false;
          },

          getGradingForm: () => {
            const { selectedModel, partner, gradingFormAbortController } =
              get();

            gradingFormAbortController?.abort();

            if (!selectedModel || !partner) {
              set({
                gradingForm: null,
                loadingGradingForm: false,
              });
              return;
            }

            const controller = new AbortController();
            set({
              loadingGradingForm: true,
              gradingFormAbortController: controller,
            });

            apiClient()
              .models[':modelId']['grading-form'].$get(
                {
                  param: {
                    partnerId: partner.id,
                    modelId: selectedModel.Id.toString(),
                  },
                },
                { init: { signal: controller.signal } },
              )
              .then((res) => res?.json())
              .then((gradingForm) =>
                set({
                  gradingForm,
                  loadingGradingForm: false,
                }),
              )
              .catch(catchAbortError);
          },
          setGradingAnswer: (questionId, answerId) => {
            set(({ gradingAnswers }) => ({
              gradingAnswers: {
                ...gradingAnswers,
                [questionId]: answerId,
              },
            }));
          },
          setGradingExtra: (extraId, answer) => {
            set(({ gradingExtras }) => ({
              gradingExtras: {
                ...gradingExtras,
                [extraId]: answer,
              },
            }));
          },

          getPriceQuote: async () => {
            const {
              priceQuoteAbortController,
              selectedModel,
              gradingExtras,
              gradingAnswers,
              partner,
            } = get();

            priceQuoteAbortController?.abort();

            if (!selectedModel || !partner) return false;

            const modelId = selectedModel.Id;

            const answers = gradingAnswers
              ? Object.entries(gradingAnswers).reduce(
                  (answers, [questionId, answerId]) => ({
                    ...answers,
                    [questionId]: [answerId],
                  }),
                  {},
                )
              : {};

            const extras = gradingExtras
              ? Object.entries(gradingExtras).reduce<number[]>(
                  (extras, [extraId, answer]) =>
                    answer ? [...extras, parseInt(extraId)] : extras,
                  [],
                )
              : [];

            const controller = new AbortController();
            set({
              loadingPriceQuote: true,
              priceQuoteAbortController: controller,
            });

            const response = await apiClient()
              ['price-quote'].$post(
                {
                  param: {
                    partnerId: partner.id,
                  },
                  json: {
                    modelId,
                    answers,
                    extras,
                  },
                },
                { init: { signal: controller.signal } },
              )
              .catch(catchAbortError);

            if (response?.ok) {
              set({
                priceQuote: await response.json(),
                loadingPriceQuote: false,
              });

              return true;
            }

            return false;
          },

          setDetails: (details) => set({ details }),
          setMarketingConsent: (marketingConsent) => set({ marketingConsent }),

          registerTradeIn: async () => {
            const {
              partner,
              priceQuote,
              deviceIdentifier,
              details,
              clientId,
              orderId,
              sku,
              purchaseUrl,
              marketingConsent,
            } = get();

            if (!partner || !priceQuote) return false;

            set({ postingTradeIn: true });

            const previousTradeInId = sessionStorage.getItem(SESSION_ID_KEY);

            const response = await apiClient().tradeins.$post({
              param: {
                partnerId: partner.id,
              },
              json: {
                clientId,
                orderId,
                sku,
                purchaseUrl,
                deviceIdentifier,
                priceQuoteId: priceQuote.PriceQuoteId,
                details,
                marketingConsent,
                previousTradeInId,
              },
            });

            if (!response.ok) {
              set({ postingTradeIn: false });
              return false;
            }

            const tradeIn = await response.json();

            set({
              tradeIn,
              postingTradeIn: false,
            });

            if (partner.featureToggles.clientSession) {
              // This is used for the confirmation page script
              sessionStorage.setItem(SESSION_ID_KEY, tradeIn.id);
            }

            return true;
          },
          setLocale: (locale) => set({ locale }),
          getTradeIn: async (tradeInId) => {
            const { partner } = get();

            if (!partner || !tradeInId) return;

            set({ loadingTradeIn: true });

            const response = await apiClient().tradeins[':tradeInId'].$get({
              param: {
                partnerId: partner?.id,
                tradeInId,
              },
            });

            if (!response.ok) {
              set({ loadingTradeIn: false });
              return;
            }

            const tradeIn = await response.json();

            if (partner.featureToggles.clientSession) {
              // This is used for the confirmation page script
              sessionStorage.setItem(SESSION_ID_KEY, tradeIn.id);
            }

            set({
              tradeIn,
              loadingTradeIn: false,
            });
          },
          updateTradeIn: async (updates) => {
            const { partner, tradeIn } = get();

            if (!partner || !tradeIn) return;

            set({ loadingTradeIn: true });

            const response = await apiClient().tradeins[':tradeInId'].$patch({
              param: {
                partnerId: partner.id,
                tradeInId: tradeIn.id,
              },
              json: updates,
            });

            if (!response.ok) {
              set({ loadingTradeIn: false });
              return;
            }

            const updatedTradeIn = await response.json();

            set({
              tradeIn: updatedTradeIn,
              loadingTradeIn: false,
            });
          },
        };
      },
      {
        name: STATE_PERSIST_KEY,
        storage: createJSONStorage(() => sessionStorage),
        partialize: ({ campaignCode }) => ({ campaignCode }),
      },
    ),
  ),
);
